import { DateTime } from "luxon";
import { CSRService } from "./CSRService";
import { Attachment, AttachmentUploadResponse, Job, JobFetchResponse } from "../models/Job";
import { NoteData } from "../models/NoteData";
import { InstallerJobQuestionnaire } from "../models/InstallerJobQuestionnaire";

/**
 * Service for manipulation jobs from the API
 */
export class JobService extends CSRService {
    public constructor() {
        super();
    }

    /**
     * Fetches jobs from the API
     * @param city - The city to filter jobs by
     * @param date - The date to filter jobs by
     * @returns A list of jobs
     */
    public fetchJobsAsync = async (city: string, date?: string): Promise<JobFetchResponse | undefined> => {
        const params = new URLSearchParams();
        if (city) {
            params.append("warehouse", city);
        }
        if (date) {
            params.append("date_requested", date ? DateTime.fromISO(date).toFormat("yyyy-MM-dd") : "");
        }

        // Fetch job data
        const jobsResponse = await JobService.axiosInstance.get<JobFetchResponse>(`/api/jobs?${params.toString()}`);
        return jobsResponse.data;
    };

    public fetchJobByIdAsync = async (jobId: string): Promise<Job | undefined> => {
        try {
            const jobResponse = await JobService.axiosInstance.get<Job>(`/api/jobs/${jobId}`);
            return jobResponse.data;
        } catch (error) {
            return undefined;
        }
    };

    /**
     * Exports jobs to CSV format
     * @param city - The city to filter jobs by
     * @param date - The date to filter jobs by
     * @param isAssignmentPage - Whether to filter for assignment page
     */
    public exportJobsToCSV = async (city: string, date?: string, isAssignmentPage = false): Promise<void> => {
        const params = new URLSearchParams();
        if (city) {
            params.append("warehouse", city);
        }
        if (date) {
            params.append("date_requested", date ? DateTime.fromISO(date).toFormat("yyyy-MM-dd") : "");
        }
        if (isAssignmentPage) {
            params.append("is_assignment_page", "true");
        }

        const response = await JobService.axiosInstance.get(`/api/jobs/export?${params.toString()}`, {
            responseType: "blob",
        });

        // Create a download link and trigger it
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `jobs_export_${DateTime.now().toFormat("yyyyMMdd_HHmmss")}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    /**
     * Assigns a worker to a job
     * @param jobId - The ID of the job to assign the worker to
     * @param workerId - The ID of the worker to assign to the job
     */
    public assignWorkerToJobAsync = async (jobId: string, workerId: string | null): Promise<void> => {
        await JobService.axiosInstance.put(`/api/jobs/${jobId}/assign`, null, {
            params: { worker_id: workerId },
            headers: { Accept: "application/json" },
        });
    };

    /**
     * Imports new jobs from the API
     * @returns A promise that resolves when the import is complete
     */
    public importNewJobsAsync = async ({ warehouse, date }: { warehouse?: string; date?: string }): Promise<void> => {
        await JobService.axiosInstance.post("/api/import/jobs/start", null, {
            params: {
                warehouse,
                date: date ? DateTime.fromISO(date).toFormat("yyyy-MM-dd") : undefined,
                async: false,
            },
            signal: AbortSignal.timeout(10 * 60 * 1000), // 10 minutes
            timeout: 10 * 60 * 1000, // 10 minutes
        });
    };

    // TODO: this method needs to move to a different service
    // it is not related to jobs
    /*
     * Creates a new job note
     * @param noteData - Note content and metadata
     * @returns Created note object
     */
    public upsertNoteAsync = async (noteData: NoteData): Promise<void> => {
        if (noteData.note_id) {
            return await JobService.axiosInstance.put("/api/jobnotes", noteData);
        }
        await JobService.axiosInstance.post("/api/jobnotes", noteData);
    };

    public getJobNotesAsync = async (jobId: string): Promise<NoteData[]> => {
        try {
            const response = await JobService.axiosInstance.get<NoteData[]>(`/api/jobnotes`, {
                params: { job_id: jobId },
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching job notes:", error);
            return [];
        }
    };

    public updateJobDateRequested = async (jobId: string, newDate: string): Promise<Job | undefined> => {
        return await JobService.axiosInstance.put(`/api/jobs/${jobId}/update-requested-date`, {
            date_requested: newDate,
        });
    };

    public uploadJobFile = async (
        jobId: string,
        file: File,
        stage: string,
        userId?: string,
        workerId?: string,
        onUploadProgress?: (progress: number, file: File) => void,
    ): Promise<Attachment | null> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("job_stage", stage);
        if (userId) {
            formData.append("user_id", userId);
        }
        if (workerId) {
            formData.append("worker_id", workerId);
        }
        try {
            const response = await JobService.axiosInstance.post<AttachmentUploadResponse>(
                `/api/job/${jobId}/upload_attachment`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: (progressEvent) => {
                        if (onUploadProgress) {
                            const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
                            onUploadProgress(progress, file);
                        }
                    },
                },
            );
            return response.data.attachment;
        } catch (ex) {
            console.error("Error uploading file:", ex);
            throw ex;
        }
    };

    public getJobAttachments = async (jobId: string): Promise<Attachment[]> => {
        try {
            const response = await JobService.axiosInstance.get<{ attachments: Attachment[] }>(
                `/api/job/${jobId}/attachments`,
            );
            return response.data.attachments;
        } catch (ex) {
            console.error("Error fetching job attachments:", ex);
            return [];
        }
    };

    public deleteJobAttachment = async (attachmentId: string): Promise<void> => {
        await JobService.axiosInstance.delete(`/api/job/attachments/${attachmentId}`);
    };

    public submitJob = async (jobId: string): Promise<void> => {
        await JobService.axiosInstance.put(`/api/job/${jobId}/submit`);
    };

    public downloadMediaReport = async (jobId: string): Promise<void> => {
        const response = await JobService.axiosInstance.post(`/api/job/${jobId}/media-report`, {
            responseType: "blob",
        });
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `media_report_${jobId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    public getQuestionnaire = async (jobId: string): Promise<InstallerJobQuestionnaire[]> => {
        try {
            const response = await JobService.axiosInstance.get<InstallerJobQuestionnaire[]>(
                `/api/job/${jobId}/questionnaire`,
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching job questionnaire:", error);
            return [];
        }
    };

    public submitQuestionnaire = async (
        jobId: string,
        questionnaire: InstallerJobQuestionnaire,
    ): Promise<InstallerJobQuestionnaire | undefined> => {
        try {
            const response = await JobService.axiosInstance.post(
                `/api/job/${jobId}/questionnaire/submit`,
                questionnaire,
            );
            return response.data;
        } catch (error) {
            console.error("Error submitting job questionnaire:", error);
            return undefined;
        }
    };
}

export const jobService = new JobService();
